// PRD 11 §2.2 #18 / §3.6 — WYSIWYG editor for Text content blocks.
// Built on `document.execCommand` to match the prototype 1:1 (see
// prototype/pages/consultation-manual.html `richEditorHtml` / `bindRichEditor`).
// Reuses the shared `mg-rte-*` CSS classes (globals.css) already used by the
// Management Guidelines editor so no new styles are introduced.
//
// The editor is `contenteditable` and writes back via `onChange` on input or
// toolbar command. It is uncontrolled internally — `value` seeds the DOM on
// mount and when the block identity changes, so the caret isn't reset on every
// keystroke.

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { useRichTextSize } from '@/shared/hooks/use-rich-text-size';
import { hasNonHttpsLink, normalizeRichHtml } from '@/shared/utils/normalize-rich-html';
import { TEXT_SIZES } from '@/shared/utils/rich-text-format';

import { isHttpsUrl } from '../utils/validate-form';

const COLORS: ReadonlyArray<{ value: string; titleKey: string }> = [
  { value: '#111827', titleKey: 'editor.colors.black' },
  { value: '#DC2626', titleKey: 'editor.colors.red' },
  { value: '#D97706', titleKey: 'editor.colors.orange' },
  { value: '#16A34A', titleKey: 'editor.colors.green' },
  { value: '#2563EB', titleKey: 'editor.colors.blue' },
  { value: '#7C3AED', titleKey: 'editor.colors.purple' },
];

interface RichTextEditorProps {
  blockId: number;
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  // Read-only (CONTENT_LIB:READ detail view, PRD §7): hide the toolbar and lock
  // the content area so the body cannot be edited.
  readOnly?: boolean;
}

function isEditorEmpty(el: HTMLElement): boolean {
  if (!el.innerHTML || el.innerHTML === '<br>') return true;
  if (el.querySelector('img,video,iframe')) return false;
  return !el.textContent?.trim();
}

export function RichTextEditor({ blockId, value, onChange, placeholder, readOnly = false }: RichTextEditorProps) {
  const { t } = useTranslation('counseling-manual');
  const contentRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLSpanElement>(null);

  // Seed the DOM on mount, on block-identity change, and whenever `value` changes
  // while the editor is NOT focused. The not-focused guard preserves the caret
  // during active typing (re-seeding mid-edit would reset it), while still
  // re-syncing the uncontrolled DOM to the incoming value when the panel is
  // reopened. Without this, closing the panel with unsaved edits and reopening
  // the same block (same blockId) left the stale DOM in place — interacting with
  // it then pushed that stale text back into form state (SHCT-526).
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== value) el.innerHTML = value;
    el.dataset.empty = isEditorEmpty(el) ? 'true' : 'false';
  }, [blockId, value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const palette = paletteRef.current;
      if (!palette) return;
      if (!palette.classList.contains('--open')) return;
      if (palette.contains(e.target as Node)) return;
      palette.classList.remove('--open');
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const commit = () => {
    const el = contentRef.current;
    if (!el) return;
    el.dataset.empty = isEditorEmpty(el) ? 'true' : 'false';
    // SHCT-453 — normalize execCommand's legacy tags (<b>/<i>/<strike>/<font>) to
    // the canonical storage tags so the BE sanitizer preserves the formatting.
    // SHCT-370 Change 4 — `httpsOnlyLinks` mirrors this feature's server-side
    // `sanitizeHttpsOnly`, so an href the save would delete never survives the editor.
    // SHCT-871 — `explicitDefaultSize` stamps 기본(16px) onto text the author never resized.
    // Without it the patient app renders that text at its own 14px fallback, so 기본 came out
    // looking like 작게. Only the committed value is stamped; a paste is normalized without it
    // and picks the size up from the commit that follows.
    onChange(normalizeRichHtml(el.innerHTML, { httpsOnlyLinks: true, explicitDefaultSize: true }));
  };

  // SHCT-871 — 크기 dropdown state: disabled inside a 소제목, and showing the size written at the caret.
  const { sizeKey, sizeLocked, paragraphTag, applySize, refresh } =
    useRichTextSize(contentRef, commit);

  const exec = (command: string, arg?: string) => {
    const el = contentRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false, arg);
    commit();
    // SHCT-871 — formatBlock changes the BLOCK under the caret without moving the selection, so
    // `selectionchange` is not guaranteed to fire and the 크기 lock would not notice that the line
    // just became a 소제목. Recompute after every command: picking 소제목 from the 문단 dropdown is
    // the route an author actually takes to a heading, and QC re-tested on exactly it.
    refresh();
  };

  // SHCT-473 — a paste carries its formatting as inline CSS (`font-weight: 700`,
  // `color: rgb(…)`), which the BE style allow-list drops on save, so the copied
  // style survived the editor but vanished after a reload. Normalize the clipboard
  // HTML to the canonical tags BEFORE it enters the DOM, so the editor, the preview
  // and the stored value all agree. Plain-text-only pastes need no handling — the
  // browser's own insertion is already canonical and `onInput` commits it.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return;
    e.preventDefault();
    // SHCT-370 Change 4 — say so before dropping the link. Reuses the message the link button
    // already shows for a typed non-https URL: same rule, so the same words rather than new copy.
    if (hasNonHttpsLink(html)) toast.error(t('editor.linkHttpsOnly'));
    document.execCommand('insertHTML', false, normalizeRichHtml(html, { httpsOnlyLinks: true }));
    commit();
  };

  const handleColorClick = (color: string) => {
    paletteRef.current?.classList.remove('--open');
    exec('foreColor', color);
  };

  const handleLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt(t('editor.linkPrompt'), 'https://');
    if (!url) return;
    // ADM.016 — hyperlinks must be https://; reject http / invalid.
    if (!isHttpsUrl(url)) {
      toast.error(t('editor.linkHttpsOnly'));
      return;
    }
    exec('createLink', url.trim());
  };

  const togglePalette = (e: React.MouseEvent) => {
    e.stopPropagation();
    paletteRef.current?.classList.toggle('--open');
  };

  return (
    <div className="mg-rte-wrap" data-rte-wrap={blockId}>
      {readOnly ? null : (
      <div
        className="mg-rte-toolbar"
        onMouseDown={(e) => {
          // Keep the caret in the editor when a toolbar button is pressed, but
          // let native <select> dropdowns (Paragraph / Font Size) open —
          // preventDefault on their mousedown suppresses the popup (SHCT-452,
          // same fix as SHCT-420 in Health Encyclopedia).
          if (!(e.target as HTMLElement).closest('select')) e.preventDefault();
        }}
      >
        {/* SHCT-871 / PRD 10 §3.7 — 문단 2종. 제목1·제목3 are retired; anything left in old
            content is folded into 소제목 by normalizeRichHtml on the next save. */}
        <select
          className="mg-rte-sel"
          title={t('editor.toolbar.paragraph')}
          // SHCT-871 — controlled, so the control names the block the caret is in rather than
          // the last value picked. The tag is passed bare, as the prototype's rteText.applyFormat
          // does and the other two already did.
          value={paragraphTag}
          onChange={(e) => exec('formatBlock', e.target.value)}
        >
          <option value="p">{t('editor.paragraphs.body')}</option>
          <option value="h2">{t('editor.paragraphs.subheading')}</option>
        </select>

        {/* SHCT-871 — 본문 크기 4단계, written as an explicit span (execCommand's fontSize emits
            <font>, which is not in the storage allow-list and vanishes on save). 소제목 has a fixed
            size, so the dropdown is disabled while the caret sits inside one. */}
        <select
          className="mg-rte-sel"
          // SHCT-871 — say WHY it is unusable, as the prototype's rteText.applyFormat does.
          title={t(sizeLocked ? 'editor.toolbar.sizeLocked' : 'editor.toolbar.size')}
          value={sizeKey}
          disabled={sizeLocked}
          onChange={(e) => applySize(e.target.value)}
        >
          {TEXT_SIZES.map((s) => (
            <option key={s.key} value={s.key}>{t(`editor.sizes.${s.key}`)}</option>
          ))}
        </select>

        <span className="mg-rte-div" />

        <button type="button" className="mg-rte-btn" onClick={() => exec('bold')} title={t('editor.toolbar.bold')} style={{ fontWeight: 700 }}>
          B
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('italic')} title={t('editor.toolbar.italic')} style={{ fontStyle: 'italic' }}>
          I
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('underline')} title={t('editor.toolbar.underline')} style={{ textDecoration: 'underline' }}>
          U
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('strikeThrough')} title={t('editor.toolbar.strike')} style={{ textDecoration: 'line-through' }}>
          S
        </button>

        <span className="mg-rte-color-wrap">
          <button type="button" className="mg-rte-btn" onClick={togglePalette} title={t('editor.toolbar.color')}>
            <span style={{ fontWeight: 700 }}>A</span>▾
          </button>
          <span className="mg-rte-color-palette" ref={paletteRef}>
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                className="mg-rte-swatch"
                style={{ background: c.value }}
                title={t(c.titleKey)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleColorClick(c.value);
                }}
              />
            ))}
          </span>
        </span>

        <span className="mg-rte-div" />

        <button type="button" className="mg-rte-btn" onClick={() => exec('insertUnorderedList')} title={t('editor.toolbar.bulletList')}>
          •
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('insertOrderedList')} title={t('editor.toolbar.numberedList')}>
          1.
        </button>

        <span className="mg-rte-div" />

        <button type="button" className="mg-rte-btn" onClick={() => exec('justifyLeft')} title={t('editor.toolbar.alignLeft')}>
          ◀
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('justifyCenter')} title={t('editor.toolbar.alignCenter')}>
          ≡
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('justifyRight')} title={t('editor.toolbar.alignRight')}>
          ▶
        </button>

        <span className="mg-rte-div" />

        <button type="button" className="mg-rte-btn" onClick={handleLink} title={t('editor.toolbar.link')}>
          🔗
        </button>
        <button type="button" className="mg-rte-btn" onClick={() => exec('formatBlock', 'blockquote')} title={t('editor.toolbar.quote')}>
          &ldquo;
        </button>
      </div>
      )}

      <div
        ref={contentRef}
        className="mg-rte-content"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-empty="true"
        onInput={readOnly ? undefined : commit}
        onBlur={readOnly ? undefined : commit}
        onPaste={readOnly ? undefined : handlePaste}
      />
    </div>
  );
}
